import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import logo from '../../sidebar-logo.png';
import screenshot from './audioread screenshot.svg';
import mixpanel from 'mixpanel-browser';
import {
  Business as BusinessIcon,
  Code as CodeIcon,
  ArrowForward as ArrowForwardIcon,
  VolumeUp as VolumeUpIcon,
  Devices as DevicesIcon,
  Language as LanguageIcon,
  AutoAwesome as AutoAwesomeIcon,
  Security as SecurityIcon,
  CloudUpload as CloudUploadIcon,
  PlayArrow as PlayArrowIcon,
  RssFeed as RssFeedIcon,
  Extension as ExtensionIcon,
  PhoneIphone as PhoneIphoneIcon,
  Computer as ComputerIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Accessibility as AccessibilityIcon,
  Star as StarIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { CircularProgress } from "@mui/material";
import { setLandingPage } from '../../utils/trackingParams';
import { logConversion } from '../../firebase';
import userStore from '../../Stores/User';
import { isPaidProUser, hasPaidPlan } from '../../constants/plans';
// Remove unused PlayerPubSub imports
import './landingA.scss';


// Lazy load non-critical components
const PricingCards = lazy(() => import('../../Components/PricingCards'));
const Testimonials = lazy(() => import('./Components/Testimonials'));
// Fallback for requestIdleCallback
const requestIdleCallback = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));

const ProBadge = observer(() => {
    const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
      ? isPaidProUser(userStore.user)
      : false;
    const hasPlan = hasPaidPlan(userStore.user);
  
    const [showPopover, setShowPopover] = useState(false);
  
    const badgeStyle = {
      background: isProUser 
        ? 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)' 
        : hasPlan 
          ? 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)'
          : 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
      padding: '2px 6px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: 'bold',
      position: 'absolute',
      top: isProUser ? '35px' : '0px',
      left: '45px',
      display: 'inline-flex',
      alignItems: 'center',
      height: '16px',
      width: isProUser ? '32px' : hasPlan ? '70px' : '95px',
      justifyContent: 'center',
      cursor: isProUser ? 'default' : 'pointer',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      color: 'white'
    };
  
    const popoverStyle = {
      position: 'absolute',
      top: '25px',
      left: '45px',
      background: 'rgba(0, 0, 0, 0.9)',
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '12px',
      width: '200px',
      color: 'white',
      zIndex: 1000,
      pointerEvents: 'none',
      opacity: showPopover ? 1 : 0,
      transition: 'opacity 0.2s ease-in-out',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
      backdropFilter: 'blur(5px)'
    };
  
    if (!userStore.user) return null;
  
    if (isProUser) {
      return (
        <div style={badgeStyle}>
          PRO
        </div>
      );
    }
  
    if (hasPlan) {
      return (
        <div style={{ position: 'relative' }}>
          <a 
            href={userStore.customerPortalUrl} 
            target="_blank" 
            rel="noreferrer"
            style={badgeStyle}
            onMouseEnter={() => setShowPopover(true)}
            onMouseLeave={() => setShowPopover(false)}
            onClick={(e) => {
              if (!userStore.customerPortalUrl) {
                e.preventDefault();
              }
            }}
          >
            UPGRADE
          </a>
          <div style={popoverStyle}>
            Upgrade to pro to get premium voices, other languages, and more!
          </div>
        </div>
      );
    }
  
    return (
      <div style={{ position: 'relative' }}>
        <Link 
          to="/onboarding/ChooseVoice" 
          style={badgeStyle}
          onMouseEnter={() => setShowPopover(true)}
          onMouseLeave={() => setShowPopover(false)}
        >
          START TRIAL
        </Link>
        <div style={popoverStyle}>
          Start your 7-day free trial today!
        </div>
      </div>
    );
  });
  
  const LandingA = observer(({ partnerConfig }) => {
  // Simple auth check - if user is signed in, redirect to /add
  useEffect(() => {
    const isSignedIn = localStorage.getItem('isSignedIn') === 'true';
    if (isSignedIn) {
      window.location.replace('/add');
    }
  }, []);

    console.log('LandingA: User is not signed in, rendering landing page');
    const location = useLocation();
    const history = useHistory();
    const queryParams = new URLSearchParams(location.search);
    const [vipId, setVipId] = useState(queryParams.get('vip'));
    const cta = "Start Listening Now";

    // Critical operations only - defer everything else
    useEffect(() => {
      // Only handle VIP ID immediately if present
      if (vipId) {
        setVipId(vipId);
      }
    }, [vipId]);

    // Add via parameter to URL on page load if partner has it configured
    useEffect(() => {
      if (partnerConfig?.via) {
        const currentParams = new URLSearchParams(location.search);
        // Only add via if it's not already in the URL
        if (!currentParams.has('via')) {
          currentParams.set('via', partnerConfig.via);
          history.replace({
            pathname: location.pathname,
            search: currentParams.toString()
          });
        }
      }
    }, [partnerConfig, location, history]);

    // Track page visit - defer until after page load to avoid blocking render
    useEffect(() => {
      // Track page visit after page is fully loaded and idle
      const trackPageVisit = () => {
        try {
          if (typeof mixpanel !== 'undefined' && mixpanel.track) {
        mixpanel.track('Landing Page Visited', {
          page: 'landing_a',
          variant: 'A',
          partner: partnerConfig?.name || null,
          utm_source: partnerConfig?.utmSource || null
        });
          }
        setLandingPage('landing_a');
        } catch (e) {
          console.warn('Tracking error:', e);
        }
      };

      // Wait for page load, then use requestIdleCallback with timeout
      if (document.readyState === 'complete') {
        requestIdleCallback(trackPageVisit, { timeout: 2000 });
      } else {
        window.addEventListener('load', () => {
          requestIdleCallback(trackPageVisit, { timeout: 2000 });
        }, { once: true });
      }
    }, [partnerConfig]);

    // Performance monitoring for LCP optimization
    useEffect(() => {
      if ('PerformanceObserver' in window) {
        try {
          const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.entryType === 'largest-contentful-paint') {
                // Track LCP performance - defer to avoid blocking
                requestIdleCallback(() => {
                  mixpanel.track('LCP Performance', {
                    value: Math.round(entry.startTime),
                    page: 'landing_a'
                  });
                });
              }
            }
          });
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
          return () => observer.disconnect();
        } catch (e) {
          console.warn('PerformanceObserver not supported');
        }
      }
    }, []);

    // Service worker registration for better caching
    useEffect(() => {
      if ('serviceWorker' in navigator) {
        requestIdleCallback(() => {
          navigator.serviceWorker.register('/sw.js')
            .catch(error => {
              console.warn('SW registration failed:', error);
            });
        });
      }
    }, []);

  
    const renderSignUpButton = useCallback(() => {
      const handleSignUpClick = () => {
        // Defer mixpanel tracking to avoid blocking render
        requestIdleCallback(() => {
          try {
            if (typeof mixpanel !== 'undefined' && mixpanel.track) {
          mixpanel.track('Click Sign Up (Nav)', {
            placement: 'Chrome',
            variant: 'A',
            experiment: 'landing_page_test'
          });
            }
          logConversion('sign_up_click', 'A');
          } catch (e) {
            console.warn('Tracking error:', e);
          }
        }, { timeout: 100 });
      };

      return (
        <div className="cta-div" style={{ position: 'static', width: 'auto' }}>
          <Link
            to="/signin?source=nav_signup&redirected_from=landingA"
            className="cta-button"
            onClick={handleSignUpClick}
          >
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div>
                <div>Start listening</div>
              </div>
            </div>
          </Link>
        </div>
      );
    }, []);
  
      const renderCta = useCallback((placement = '', buttonText = null) => {
        // Build the redirect URL with partner info if available
        let redirectUrl = "/signin?source=landingA&redirected_from=landingA";
        if (partnerConfig) {
          redirectUrl += `&utm_source=${partnerConfig.utmSource}`;
        }
        // Add via parameter for tolt.io referral tracking - check both partnerConfig and URL params
        const urlParams = new URLSearchParams(location.search);
        const viaParam = partnerConfig?.via || urlParams.get('via');
        if (viaParam) {
          redirectUrl += `&via=${viaParam}`;
        }
        
        const handleCtaClick = () => {
          // Defer mixpanel tracking to avoid blocking render
          requestIdleCallback(() => {
            try {
              if (typeof mixpanel !== 'undefined' && mixpanel.track) {
            mixpanel.track('Click Try For Free', {
              placement: placement,
              variant: 'A',
              experiment: 'landing_page_test',
              partner: partnerConfig?.name || null,
              utm_source: partnerConfig?.utmSource || null
            });
              }
            logConversion('try_for_free_click', 'A');
            } catch (e) {
              console.warn('Tracking error:', e);
            }
          }, { timeout: 100 });
        };
        
        const displayText = buttonText || cta;
        
        return (
          <div className="cta-div" style={{ position: 'static' }}>
            <Link
              to={redirectUrl}
              className="cta-button"
              onClick={handleCtaClick}
            >
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                <div>
                  <div>{displayText}</div>
                </div>
              </div>
            </Link>
          </div>
        );
      }, [partnerConfig, cta, location]);
  
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Helper function to estimate audio duration
    const estimateAudioDuration = useCallback((chars) => {
      const wordsPerMinute = 200; // Average reading speed
      const minutes = chars / (wordsPerMinute * 5); // 5 characters per word
      return Math.round(minutes * 10.5); // 10.5 seconds per minute
    }, []);


  
    return (
      <div className="App" style={{ contain: 'layout style paint' }}>
        <Helmet>
          <title>The Smarter Way to Listen | AI Text-to-Speech by AudioRead</title>
          <meta name="description" content="Convert words to sound with cutting-edge AI. Listen to anything, anywhere fast, natural, and effortless. Try AudioRead today." />
          <meta property="og:title" content="The Smarter Way to Listen | AI Text-to-Speech by AudioRead" />
          <meta property="og:description" content="Convert words to sound with cutting-edge AI. Listen to anything, anywhere fast, natural, and effortless. Try AudioRead today." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:image" content="https://audioread.com/logo.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="The Smarter Way to Listen | AI Text-to-Speech by AudioRead" />
          <meta name="twitter:description" content="Convert words to sound with cutting-edge AI. Listen to anything, anywhere fast, natural, and effortless. Try AudioRead today." />
          <meta name="twitter:image" content="https://audioread.com/logo.png" />
        </Helmet>
        {/* Preload critical assets for LCP */}
        <link rel="preload" href={logo} as="image" />
        <link rel="preload" href={screenshot} as="image" />
        <header
          className="App-header"
          style={{
            minHeight: '100vh',
            marginTop: partnerConfig ? '30px' : '0px'
          }}
        >
          <div className="nav-bar" style={{ 
            paddingTop: partnerConfig ? '20px' : '0px'
          }}>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <div style={{ position: "relative" }} className="logo-container">
              <img 
                className="audiblogs-logo" 
                src={logo} 
                alt="logo" 
                loading="eager"
                decoding="sync"
                style={{ contain: 'layout style paint' }}
              />
                <ProBadge />
              </div>
              <div style={{ marginLeft: '50px' }} className="ph-badge">
                <a
                  href="https://www.producthunt.com/posts/audiblogs?utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-audiblogs"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=283759&theme=dark&period=monthly"
                    alt="Product Hunt Top Post Badge - Audioread"
                    style={{
                      width: '250px',
                      height: '54px',
                      contain: 'layout style paint'
                    }}
                    width="250"
                    height="54"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
                <Link
                  className="signin-button"
                  to="/signin?source=nav_signin&redirected_from=landingA"
                  onClick={(e) => {
                    // Defer mixpanel tracking to avoid blocking render
                    requestIdleCallback(() => {
                      mixpanel.track('Click Log In on Landing Page');
                    });
                  }}
                >
                  Sign In
                </Link>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', marginLeft: '-20px'}}>
                {renderSignUpButton()}
                <button 
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    marginLeft: '5px',
                    width: '44px',
                    height: '44px',
                    minWidth: '44px',
                    minHeight: '44px',
                    flexShrink: 0
                  }}
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
                </button>
              </div>
            </div>
  
            {/* Mobile Navigation */}
            <div className="mobile-nav">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', paddingBottom: '15px', marginRight: '-10px'}}>
                {renderSignUpButton()}
                <button 
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    marginLeft: '5px',
                    width: '44px',
                    height: '44px',
                    minWidth: '44px',
                    minHeight: '44px',
                    flexShrink: 0
                  }}
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
                </button>
              </div>
            </div>
          </div>
          
          {/* Navigation Menu (shared for desktop and mobile) - Outside nav-bar to prevent clipping */}
          {isMenuOpen && (
            <div 
              style={{
                position: 'fixed',
                top: '80px',
                right: '20px',
                backgroundColor: 'rgba(9, 2, 19, 0.95)',
                padding: '20px',
                width: '220px',
                borderRadius: '10px',
                backdropFilter: 'blur(10px)',
                zIndex: 10000,
                boxSizing: 'border-box',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                overflow: 'visible'
              }}
            >
              <div style={{display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Link
                  to="/features"
                  className="mobile-menu-link"
                  onClick={() => {
                    mixpanel.track('Click Features from Menu');
                    setIsMenuOpen(false);
                  }}
                  style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
                >
                  Features
                </Link>
                <Link
                  to="/how-it-works"
                  className="mobile-menu-link"
                  onClick={() => {
                    mixpanel.track('Click How It Works from Menu');
                    setIsMenuOpen(false);
                  }}
                  style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
                >
                  How It Works
                </Link>
                <Link
                  to="/integrations-and-platforms"
                  className="mobile-menu-link"
                  onClick={() => {
                    mixpanel.track('Click Integrations from Menu');
                    setIsMenuOpen(false);
                  }}
                  style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
                >
                  Integrations
                </Link>
                <Link
                  to="/solutions-and-accessibility"
                  className="mobile-menu-link"
                  onClick={() => {
                    mixpanel.track('Click Solutions from Menu');
                    setIsMenuOpen(false);
                  }}
                  style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
                >
                  Solutions
                </Link>
                <Link
                  to="/organizations"
                  className="mobile-menu-link"
                  onClick={() => {
                    mixpanel.track('Click Organizations from Menu');
                    setIsMenuOpen(false);
                  }}
                  style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
                >
                  Organizations
                </Link>
                <Link
                  to="/api"
                  className="mobile-menu-link"
                  onClick={() => {
                    mixpanel.track('Click API from Menu');
                    setIsMenuOpen(false);
                  }}
                  style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
                >
                  API
                </Link>
              </div>
            </div>
          )}
  
          <h1 className="bigger-headline" style={{ 
            marginTop: partnerConfig ? '40px' : '220px',
            paddingTop: partnerConfig ? '20px' : '0px',
            // Ensure immediate text rendering for LCP
            fontDisplay: 'swap',
            willChange: 'auto',
            contain: 'layout style paint'
          }}>
            {partnerConfig?.customContent?.headline || 'Listen While You Do More'}
          </h1>
  
          <h2 className="subheadline">
            {partnerConfig?.customContent?.subheadline ? (
              partnerConfig.customContent.subheadline
            ) : (
              <>
                Turn text into voice with Audioread, the text to speech AI platform built for multitaskers. Convert articles, emails, PDFs, and more into natural, human-like audio and stream them in any podcast app or browser.
              </>
            )}
          </h2>
          {renderCta(partnerConfig?.customContent?.ctaText || 'Start Listening Now')}
          
          {/* Social Proof */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            marginTop: '40px',
            marginBottom: '30px',
            padding: '20px',
            flexWrap: 'wrap'
          }}>
            {/* Profile Pictures */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '-8px',
              marginRight: '8px'
            }}>
              {[
                { emoji: '👩', color: '#f97316' },
                { emoji: '👨‍💼', color: '#3b82f6' },
                { emoji: '👩‍💻', color: '#e5e7eb' },
                { emoji: '👨‍🎓', color: '#ffffff' },
                { emoji: '👩‍🎨', color: '#06b6d4' }
              ].map((profile, index) => (
                <div
                  key={index}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: profile.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    marginLeft: index > 0 ? '-12px' : '0',
                    border: '3px solid #1f2937',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    zIndex: 5 - index
                  }}
                >
                  {profile.emoji}
                </div>
              ))}
            </div>
            
            {/* Stars and Text */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '4px'
            }}>
              {/* Stars */}
              <div style={{
                display: 'flex',
                gap: '4px',
                color: '#fbbf24',
                fontSize: '20px',
                lineHeight: '1'
              }}>
                {'★★★★★'}
              </div>
              
              {/* Text */}
              <div style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: '600',
                lineHeight: '1.2'
              }}>
                <span style={{ fontSize: '24px', fontWeight: '700' }}>100,000+</span> listeners
              </div>
            </div>
          </div>
          
          {/* LCP Image - optimized for fastest load */}
          <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            marginTop: '0px',
            marginBottom: '0px',
            width: '100%',
            padding: '0 10px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            <img 
              src={screenshot} 
              alt="Audioread interface" 
              className="screenshot"
              loading="eager"
              decoding="sync"
              width="1600"
              height="1067"
            />
          </div>
  
        </header>

        {/* Why Choose Audioread Section - Right after hero */}
        <div className="App-header" style={{ margin: '0 auto', padding: '0px 0 0px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
            <StarIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
            <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Why Choose Audioread?
            </h1>
            </div>
          <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>
            Whether you're commuting, walking, or catching up while cooking, Audioread helps you absorb more without being tied to a screen. Our powerful AI text to speech engine delivers ultra-realistic narration that's ideal for long-form listening.
          </p>
          
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 20px',
            textAlign: 'left'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              fontSize: '18px', 
              lineHeight: '1.8',
              color: '#fff',
              marginBottom: '30px'
            }}>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <VolumeUpIcon style={{ fontSize: '24px', color: '#fff' }} />
        </div>
                <div>
                  <strong style={{ background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Ultra-Realistic AI Voices:</strong> Designed to sound like professional audiobook narrators.
          </div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DevicesIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div>
                  <strong style={{ background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Cross-Device Listening:</strong> Stream content in <Link to="/integrations-and-platforms" style={{ color: '#4292fa', textDecoration: 'underline' }}>Apple Podcasts, Spotify, or any podcast player</Link> via a private RSS feed.
              </div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CloudUploadIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div>
                  <strong style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Flexible Audio Converter:</strong> Supports links, PDFs, emails, plain text, and RSS feeds.
              </div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LanguageIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div>
                  <strong style={{ background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Supports 80+ Languages:</strong> Access audio content in your preferred language.
              </div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AutoAwesomeIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div>
                  <strong style={{ background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Built-in AI Summaries:</strong> Preview key points before you listen.
              </div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #4caf50 0%, #2196F3 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SecurityIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div>
                  <strong style={{ background: 'linear-gradient(135deg, #4caf50 0%, #2196F3 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Privacy Mode:</strong> Content auto-deletes after 30 days, no tracking or sharing.
          </div>
              </li>
            </ul>
        </div>

          <p className="details" style={{ marginTop: '30px', maxWidth: '800px', margin: '30px 10px', padding: '20px', background: 'linear-gradient(135deg, rgba(203, 94, 238, 0.1) 0%, rgba(75, 225, 236, 0.1) 100%)', borderRadius: '15px', border: '1px solid rgba(203, 94, 238, 0.2)' }}>
            Experience a smarter way to consume content with Audioread: the intelligent online text to audio solution for busy professionals, students, and lifelong learners.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginTop: '120px', marginBottom: '30px' }}>
            <PlayArrowIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
            <h2 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              How Audioread Works
          </h2>
          </div>
          <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>
            You don't need to download anything to get started. Audioread's browser extension, mobile apps, and online platform let you convert text in seconds.
          </p>
          
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 20px',
            textAlign: 'left'
          }}>
            <ol style={{
              padding: 0,
              fontSize: '18px', 
              lineHeight: '1.8',
              color: '#fff',
              marginBottom: '30px',
              listStyle: 'none',
              counterReset: 'step-counter'
            }}>
              <li style={{ marginBottom: '30px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'center', counterIncrement: 'step-counter' }}>
                <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', borderRadius: '50%', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', height: '40px', fontWeight: 'bold', fontSize: '18px', color: '#fff' }}>
                  {String.fromCharCode(49)}
            </div>
                <div>Highlight or upload your text via web app, browser extension, or mobile shortcut.</div>
              </li>
              <li style={{ marginBottom: '30px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'center', counterIncrement: 'step-counter' }}>
                <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', borderRadius: '50%', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', height: '40px', fontWeight: 'bold', fontSize: '18px', color: '#fff' }}>
                  {String.fromCharCode(50)}
          </div>
                <div>Audioread's AI voice generator turns it into crystal-clear audio instantly.</div>
              </li>
              <li style={{ marginBottom: '30px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'center', counterIncrement: 'step-counter' }}>
                <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', borderRadius: '50%', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', height: '40px', fontWeight: 'bold', fontSize: '18px', color: '#fff' }}>
                  {String.fromCharCode(51)}
        </div>
                <div>Subscribe to your own private podcast feed and start listening on your chosen app.</div>
              </li>
            </ol>
          </div>
  
          <p style={{ marginTop: '30px', textAlign: 'center' }}>
            <Link to="/how-it-works" style={{ color: '#4292fa', textDecoration: 'underline', fontSize: '18px', fontWeight: 'bold' }}>Learn More About How It Works →</Link>
          </p>
          </div>

        {/* Testimonials Section */}
        <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '80px 0 60px 0' }}>
          <h1 className="headline" style={{ marginBottom: '30px' }}>
            What Our Users Say
          </h1>
          <Suspense fallback={<div style={{ textAlign: 'center', padding: '40px' }}>Loading testimonials...</div>}>
            <Testimonials />
          </Suspense>
        </div>

        {/* Key Features Section */}
        <div className="headline" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '0px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
            <AutoAwesomeIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
            <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Key Features</h1>
          </div>
          <p className="details" style={{ maxWidth: '800px', margin: '0 auto 30px' }}>
            Explore the full power of Audioread's text to speech AI platform. Our platform offers adjustable playback speed and MP3 downloads, multilingual support and browser-based playback, and much more.
          </p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Link to="/features" style={{ 
              color: '#4292fa', 
              textDecoration: 'none', 
                  fontSize: '18px', 
              fontWeight: 'bold',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.2) 0%, rgba(76, 175, 80, 0.2) 100%)',
              borderRadius: '10px',
              border: '1px solid rgba(66, 146, 250, 0.3)',
              display: 'inline-block',
              transition: 'all 0.3s ease'
            }}>Explore Full Features →</Link>
                </div>
              </div>
              
        {/* Works With Your Tools Section */}
        <div className="headline" style={{ display: 'flex', flexDirection: 'column', margin: '80px auto', padding: '60px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
            <ExtensionIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
            <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Works With Your Tools</h1>
                </div>
          <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>
            Audioread integrates with your existing workflows for fast, frictionless conversion.
          </p>
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px' }}>
            <ul style={{ textAlign: 'left', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ExtensionIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div><strong style={{ background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Browser Extensions:</strong> Available on Chrome, Edge, and Brave</div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PhoneIphoneIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
                <div><strong style={{ background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Safari Shortcuts:</strong> Convert articles directly from your iPhone or iPad</div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ComputerIcon style={{ fontSize: '24px', color: '#fff' }} />
              </div>
                <div><strong style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Android PWA:</strong> Use directly in Chrome or other Android browsers</div>
              </li>
              <li style={{ marginBottom: '25px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmailIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
                <div><strong style={{ background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Other Input Methods:</strong> Forward emails, drag and drop PDFs, copy and paste text, or use RSS feeds</div>
              </li>
            </ul>
          </div>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Link to="/integrations-and-platforms" style={{ 
              color: '#4292fa', 
              textDecoration: 'none', 
              fontSize: '18px', 
              fontWeight: 'bold',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.2) 0%, rgba(76, 175, 80, 0.2) 100%)',
              borderRadius: '10px',
              border: '1px solid rgba(66, 146, 250, 0.3)',
              display: 'inline-block',
              transition: 'all 0.3s ease'
            }}>See All Integrations & Platforms →</Link>
              </div>
            </div>

        {/* Solutions for Every Listener Section */}
        <div className="headline" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
            <SchoolIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
            <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Solutions for Every Listener</h1>
              </div>
          <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>
            Whether you're an individual looking to listen more, a team improving accessibility, or a business scaling audio content, Audioread provides the tools to convert and stream seamlessly.
          </p>
          <div className="solutions-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '20px', 
            maxWidth: '900px', 
            margin: '0 auto', 
            padding: '0 20px' 
            }}>
              <div style={{
              padding: '30px', 
              background: 'linear-gradient(135deg, rgba(203, 94, 238, 0.1) 0%, rgba(75, 225, 236, 0.1) 100%)', 
              borderRadius: '15px', 
              border: '1px solid rgba(203, 94, 238, 0.2)', 
                textAlign: 'center',
              aspectRatio: '1',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <WorkIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '10px' }} />
              <h3 style={{ color: '#fff', marginTop: '10px' }}>Individuals</h3>
              </div>
            <div style={{
              padding: '30px',
              background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)', 
              borderRadius: '15px', 
              border: '1px solid rgba(66, 146, 250, 0.2)', 
                textAlign: 'center',
              aspectRatio: '1',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <AccessibilityIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '10px' }} />
              <h3 style={{ color: '#fff', marginTop: '10px' }}>Teams</h3>
              </div>
            <Link to="/organizations" style={{ 
              padding: '30px',
              background: 'linear-gradient(135deg, rgba(133, 53, 205, 0.1) 0%, rgba(232, 33, 134, 0.1) 100%)', 
              borderRadius: '15px', 
              border: '1px solid rgba(133, 53, 205, 0.2)', 
              textAlign: 'center', 
              textDecoration: 'none', 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              aspectRatio: '1',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(133, 53, 205, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <BusinessIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '10px' }} />
              <h3 style={{ color: '#fff', marginTop: '10px' }}>Businesses</h3>
            </Link>
            <Link to="/api" style={{ 
              padding: '30px',
              background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)', 
              borderRadius: '15px', 
              border: '1px solid rgba(66, 146, 250, 0.2)', 
              textAlign: 'center', 
              textDecoration: 'none', 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              aspectRatio: '1',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(66, 146, 250, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <CodeIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '10px' }} />
              <h3 style={{ color: '#fff', marginTop: '10px' }}>Developers</h3>
            </Link>
              </div>
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <Link to="/solutions-and-accessibility" style={{ 
              color: '#4292fa', 
              textDecoration: 'none', 
              fontSize: '18px', 
              fontWeight: 'bold',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, rgba(133, 53, 205, 0.2) 0%, rgba(232, 33, 134, 0.2) 100%)',
              borderRadius: '10px',
              border: '1px solid rgba(133, 53, 205, 0.3)',
              display: 'inline-block',
              transition: 'all 0.3s ease'
            }}>View Solutions Overview →</Link>
            </div>
              </div>

        {/* Ready to Start Listening Section */}
        <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0', maxWidth: '900px', textAlign: 'center' }}>
          <h1 className="headline">Ready to Start Listening?</h1>
          <p className="details" style={{ margin: '0 auto' }}>
            Join users across the World who are transforming how they consume content.
          </p>
          <p className="details" style={{ marginTop: '20px', margin: '20px auto 0' }}>
            Audioread is the go-to audio converter for people who want to turn reading time into listening time at home, at work, or on the move.
          </p>
          <div style={{ marginTop: '30px' }}>
            {renderCta('Ready to Start Listening', 'Start Listening Today')}
          </div>
        </div>

        <div className="App-header" id="tweets" style={{ padding: '60px 0' }}>

          <div style={{
            marginTop: '0',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            maxWidth: '900px',
            width: '90%',
          }}>
            {/* Enterprise CTA */}
            <div style={{
              padding: '40px',
              background: 'linear-gradient(135deg, rgba(133, 53, 205, 0.1) 0%, rgba(232, 33, 134, 0.1) 100%)',
              borderRadius: '24px',
              textAlign: 'left',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid rgba(133, 53, 205, 0.2)',
            }}>
              {/* Background Glow Effect */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-25%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(133, 53, 205, 0.1) 0%, rgba(133, 53, 205, 0) 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }} />

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)',
                  padding: '16px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <BusinessIcon style={{ fontSize: '32px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ 
                    fontSize: '2em',
                    marginBottom: '16px',
                    background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Looking for Enterprise Solutions?
                  </h2>
                  <p style={{ 
                    fontSize: '1.2em',
                    marginBottom: '24px',
                    color: '#fff',
                    opacity: 0.9,
                    lineHeight: '1.5'
                  }}>
                    We offer custom solutions for businesses, educational institutions, and organizations.
                    Get volume discounts, team management, and dedicated support.
                  </p>
                  <Link
                    to="/organizations"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '15px 30px',
                      background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '12px',
                      fontSize: '1.1em',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 15px rgba(133, 53, 205, 0.3)'
                    }}
                    className="org-cta-button"
                    onClick={() => {
                      mixpanel.track('Click Organizations CTA', {
                        placement: 'pricing_section'
                      });
                      window.scrollTo(0, 0);
                    }}
                  >
                    Learn More About Enterprise Plans
                    <ArrowForwardIcon style={{ fontSize: '20px' }} />
                  </Link>
                </div>
              </div>
            </div>

            {/* API CTA */}
            <div style={{
              padding: '40px',
              background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)',
              borderRadius: '24px',
              textAlign: 'left',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid rgba(66, 146, 250, 0.2)',
            }}>
              {/* Background Glow Effect */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-25%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(66, 146, 250, 0.1) 0%, rgba(66, 146, 250, 0) 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }} />

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)',
                  padding: '16px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <CodeIcon style={{ fontSize: '32px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ 
                    fontSize: '2em',
                    marginBottom: '16px',
                    background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Need API Access?
                  </h2>
                  <p style={{ 
                    fontSize: '1.2em',
                    marginBottom: '24px',
                    color: '#fff',
                    opacity: 0.9,
                    lineHeight: '1.5'
                  }}>
                    Convert text to audio programmatically with our simple REST API.
                    Send text, URLs, or files and get podcast feeds for your users.
                  </p>
                  <Link
                    to="/api"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '15px 30px',
                      background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '12px',
                      fontSize: '1.1em',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 15px rgba(66, 146, 250, 0.3)'
                    }}
                    className="org-cta-button"
                    onClick={() => {
                      mixpanel.track('Click API CTA', {
                        placement: 'pricing_section'
                      });
                      window.scrollTo(0, 0);
                    }}
                  >
                    View API Documentation
                    <ArrowForwardIcon style={{ fontSize: '20px' }} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <OneGiftLogo /> */}
        <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/dmca">DMCA Policy</Link>
          <a href="https://audioread.tolt.io/" target="_blank" rel="noopener noreferrer">Affiliate Program</a>
        </div>
        <div style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          
        </div>
        <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved. | <a href="mailto:info@audioread.com" target="_blank" rel="noopener noreferrer">
            Contact us at info@audioread.com
          </a></div>
      </div>
    );
  });

export default LandingA; 